import { useCallback, useEffect, useMemo, useState } from 'react';
import type {
  ChatResolveEntityOption,
  EditableResolveDraftAction,
  EditableResolveDraftNote,
} from '../../models/admin/AdminChatResolveDraft';
import FidesChatAdminService from '../../services/admin/FidesChatAdminService';
import { orderAttachToOptions } from '../../utils/chatResolveAttachTo';
import {
  buildSelectedResolveDraftPayload,
  countSelectedResolveDrafts,
  toEditableActions,
  toEditableNotes,
  updateActionEntityInList,
  updateNoteEntityInList,
  validateResolveDraftCommitSelection,
} from './chatResolveDraftsDialogUtils';

export interface UseChatResolveDraftsDialogOptions {
  open: boolean;
  conversationId: string | null;
  entityOptions: ChatResolveEntityOption[];
  onClose: () => void;
  onSaved?: (counts: { notes: number; actions: number }) => void;
}

export function useChatResolveDraftsDialog({
  open,
  conversationId,
  entityOptions,
  onClose,
  onSaved,
}: UseChatResolveDraftsDialogOptions) {
  const [summary, setSummary] = useState('');
  const [notes, setNotes] = useState<EditableResolveDraftNote[]>([]);
  const [actions, setActions] = useState<EditableResolveDraftAction[]>([]);
  const [attachToOptions, setAttachToOptions] = useState<ChatResolveEntityOption[]>(entityOptions);
  const [draftsGenerated, setDraftsGenerated] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const resetState = useCallback(() => {
    setSummary('');
    setNotes([]);
    setActions([]);
    setAttachToOptions(orderAttachToOptions(entityOptions));
    setDraftsGenerated(false);
    setError(null);
    setIsGenerating(false);
    setIsSaving(false);
  }, [entityOptions]);

  const generateDrafts = useCallback(async () => {
    if (!conversationId) return;
    setIsGenerating(true);
    setError(null);
    try {
      const result = await FidesChatAdminService.generateResolveDrafts(conversationId);
      setSummary(result.summary);
      setNotes(toEditableNotes(result.notes));
      setActions(toEditableActions(result.actions));
      setAttachToOptions(
        orderAttachToOptions(result.entityOptions?.length ? result.entityOptions : entityOptions),
      );
      setDraftsGenerated(true);
    } catch (generateError) {
      console.error('Failed to generate resolve drafts', generateError);
      setError('Unable to generate draft notes and actions. Try again.');
    } finally {
      setIsGenerating(false);
    }
  }, [conversationId, entityOptions]);

  useEffect(() => {
    if (!open) {
      resetState();
      return;
    }
    setAttachToOptions(orderAttachToOptions(entityOptions));
  }, [open, entityOptions, resetState]);

  const selectedCounts = useMemo(
    () => countSelectedResolveDrafts(notes, actions),
    [notes, actions],
  );

  const updateNoteEntity = useCallback((draftId: string, option: ChatResolveEntityOption) => {
    setNotes((current) => updateNoteEntityInList(current, draftId, option));
  }, []);

  const updateActionEntity = useCallback(
    (draftId: string, option: ChatResolveEntityOption | null) => {
      setActions((current) => updateActionEntityInList(current, draftId, option));
    },
    [],
  );

  const commitSelected = useCallback(async (): Promise<{
    notes: number;
    actions: number;
  } | null> => {
    if (!conversationId) return null;

    const validationError = validateResolveDraftCommitSelection(notes, actions);
    if (validationError) {
      setError(validationError);
      return null;
    }

    const { selectedNotes, selectedActions } = buildSelectedResolveDraftPayload(notes, actions);

    setIsSaving(true);
    setError(null);
    try {
      const result = await FidesChatAdminService.commitResolveDrafts(conversationId, {
        notes: selectedNotes,
        actions: selectedActions,
      });
      return {
        notes: result.noteExternalIds.length,
        actions: result.actionExternalIds.length,
      };
    } catch (commitError) {
      console.error('Failed to commit resolve drafts', commitError);
      setError('Unable to save notes and actions. Please try again.');
      return null;
    } finally {
      setIsSaving(false);
    }
  }, [conversationId, notes, actions]);

  const handleSave = useCallback(async () => {
    const counts = await commitSelected();
    if (!counts) return;
    onSaved?.(counts);
    onClose();
  }, [commitSelected, onClose, onSaved]);

  return {
    summary,
    notes,
    setNotes,
    actions,
    setActions,
    attachToOptions,
    draftsGenerated,
    isGenerating,
    isSaving,
    error,
    selectedCounts,
    generateDrafts,
    updateNoteEntity,
    updateActionEntity,
    handleSave,
  };
}
